/** ANNOUNCE — one line pinned on top of the players' board.
 *
 *  "NEXT 10 MINS PITCHERS ARE $5": type it, choose how long it stays, send.
 *  Nothing else to fill in. A six-digit manager reaches one table at their own
 *  establishment or everyone at it; an admin reaches any establishment in
 *  their organization; only a super admin can reach every establishment.
 *
 *  Recent lets the same line go out again in one tap, and anything pinned can
 *  be taken off in one tap.
 */
import { useCallback, useEffect, useState } from 'react';
import { Megaphone, Send, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

export const AnnounceControl = ({ orgId, neo = {}, t }) => {
    const [data, setData] = useState(null);
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [text, setText] = useState({ en: '', fr: '', es: '' });
    const [minutes, setMinutes] = useState(10);
    const [scope, setScope] = useState('location');
    const [venue, setVenue] = useState('');
    const [table, setTable] = useState('');

    const load = useCallback(async () => {
        if (!orgId) return;
        try {
            const res = await api.get('/live-game/announce',
                { ...neo, params: { organization_id: orgId } });
            setData(res.data);
            setVenue((v) => v || res.data?.my_location_id || res.data?.venues?.[0]?.id || '');
        } catch (_e) { /* a dropped read changes nothing on screen */ }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orgId]);

    useEffect(() => { load(); }, [load]);

    const call = async (path, payload) => {
        setBusy(true);
        try {
            const res = await api.post(`/live-game/announce${path}`,
                { organization_id: orgId, ...payload }, neo);
            setData(res.data);
            return true;
        } catch (e) {
            const detail = e.response?.data?.detail;
            toast.error(detail === 'other_establishment'
                ? t('liveGame.announceOtherVenue', 'That table is at another establishment')
                : detail === 'pick_a_table'
                    ? t('liveGame.announcePickTable', 'Pick a table first')
                    : t('liveGame.announceFailed', 'Could not send that'));
            return false;
        } finally { setBusy(false); }
    };

    const send = async (words) => {
        const body = words || text;
        if (!(body.en || '').trim()) return;
        const ok = await call('', {
            text: body, minutes, scope,
            location_id: scope === 'location' ? venue : '',
            card_id: scope === 'table' ? table : '',
        });
        if (ok) {
            setText({ en: '', fr: '', es: '' });
            setOpen(false);
            toast.success(t('liveGame.announceSent', 'Pinned on the board'));
        }
    };

    if (!data?.can_send) return null;
    const active = data.active || [];

    return (
        <Card className="w-full" data-testid="live-game-announce">
            <CardContent className="space-y-3 p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <p className="flex items-center gap-2 text-sm font-semibold">
                        <Megaphone className="w-4 h-4 text-amber-500" />
                        {t('liveGame.announceTitle', 'Announcements')}
                    </p>
                    <Button className="min-h-[44px]" onClick={() => setOpen((v) => !v)}
                            data-testid="live-game-announce-open">
                        <Megaphone className="w-4 h-4 mr-2" />
                        {t('liveGame.announce', 'Announce')}
                    </Button>
                </div>

                {open && (
                    <div className="space-y-3 rounded-lg border border-border p-3"
                         data-testid="live-game-announce-box">
                        <Input value={text.en} maxLength={data.max_chars || 140}
                               onChange={(e) => setText({ ...text, en: e.target.value })}
                               placeholder={t('liveGame.announcePlaceholder', 'NEXT 10 MINS PITCHERS ARE $5')}
                               data-testid="live-game-announce-text" />
                        <div className="grid gap-2 sm:grid-cols-2">
                            <Input value={text.fr} maxLength={data.max_chars || 140}
                                   onChange={(e) => setText({ ...text, fr: e.target.value })}
                                   placeholder={t('liveGame.announceFr', 'French (optional)')}
                                   data-testid="live-game-announce-text-fr" />
                            <Input value={text.es} maxLength={data.max_chars || 140}
                                   onChange={(e) => setText({ ...text, es: e.target.value })}
                                   placeholder={t('liveGame.announceEs', 'Spanish (optional)')}
                                   data-testid="live-game-announce-text-es" />
                        </div>

                        <div className="space-y-1">
                            <p className="text-xs text-muted-foreground">
                                {t('liveGame.announceHowLong', 'How long it stays')}
                            </p>
                            <div className="flex flex-wrap gap-2">
                                {[...(data.minutes || [5, 10, 15, 30, 60]), 0].map((m) => (
                                    <button key={m} type="button" onClick={() => setMinutes(m)}
                                            data-testid={`live-game-announce-minutes-${m}`}
                                            className={`min-h-[40px] rounded-full border px-3 text-xs font-semibold ${
                                                minutes === m ? 'border-amber-500 bg-amber-500/10' : 'border-border'}`}>
                                        {m ? t('liveGame.announceMinutes', { count: m, defaultValue: '{{count}} min' })
                                            : t('liveGame.announceUntilRemoved', 'Until I remove it')}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="space-y-1">
                            <p className="text-xs text-muted-foreground">
                                {t('liveGame.announceWho', 'Who sees it')}
                            </p>
                            <div className="flex flex-wrap gap-2">
                                {[
                                    ['location', t('liveGame.announceScopeLocation', 'Everyone at this establishment')],
                                    ['table', t('liveGame.announceScopeTable', 'One table')],
                                    ...(data.can_all_establishments
                                        ? [['organization', t('liveGame.announceScopeAll', 'All establishments')]]
                                        : []),
                                ].map(([key, label]) => (
                                    <button key={key} type="button" onClick={() => setScope(key)}
                                            data-testid={`live-game-announce-scope-${key}`}
                                            className={`min-h-[40px] rounded-full border px-3 text-xs font-semibold ${
                                                scope === key ? 'border-amber-500 bg-amber-500/10' : 'border-border'}`}>
                                        {label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {scope === 'location' && (data.venues || []).length > 1 && (
                            <select value={venue} onChange={(e) => setVenue(e.target.value)}
                                    data-testid="live-game-announce-venue"
                                    className="w-full rounded-lg border border-border bg-background px-2.5 py-2 text-sm">
                                {(data.venues || []).map((v) => (
                                    <option key={v.id} value={v.id}>{v.name}</option>
                                ))}
                            </select>
                        )}

                        {scope === 'table' && (
                            <select value={table} onChange={(e) => setTable(e.target.value)}
                                    data-testid="live-game-announce-table"
                                    className="w-full rounded-lg border border-border bg-background px-2.5 py-2 text-sm">
                                <option value="">{t('liveGame.announcePickTable', 'Pick a table first')}</option>
                                {(data.tables || []).map((row) => (
                                    <option key={row.card_id} value={row.card_id}>{row.label}</option>
                                ))}
                            </select>
                        )}

                        <Button className="min-h-[44px]" disabled={busy || !text.en.trim()}
                                onClick={() => send()} data-testid="live-game-announce-send">
                            <Send className="w-4 h-4 mr-2" />
                            {t('liveGame.announceSend', 'Send')}
                        </Button>
                    </div>
                )}

                {active.length > 0 && (
                    <div className="space-y-2" data-testid="live-game-announce-active">
                        {active.map((a) => (
                            <div key={a.id} className="flex items-center justify-between gap-3 rounded-lg border border-amber-500/40 bg-amber-500/5 px-3 py-2"
                                 data-testid="live-game-announce-active-row">
                                <div className="min-w-0">
                                    <p className="truncate text-sm font-semibold">{a.text?.en}</p>
                                    <p className="text-[11px] text-muted-foreground">
                                        {a.sender_name} · {a.until_removed
                                            ? t('liveGame.announceUntilRemoved', 'Until I remove it')
                                            : t('liveGame.announceMinutes', { count: a.minutes, defaultValue: '{{count}} min' })}
                                    </p>
                                </div>
                                <Button size="sm" variant="outline" disabled={busy}
                                        onClick={() => call('/remove', { announcement_id: a.id })}
                                        data-testid="live-game-announce-remove">
                                    <X className="w-3.5 h-3.5 mr-1.5" />
                                    {t('liveGame.announceRemove', 'Remove')}
                                </Button>
                            </div>
                        ))}
                    </div>
                )}

                {(data.recent || []).length > 0 && (
                    <div className="space-y-1" data-testid="live-game-announce-recent">
                        <p className="text-xs text-muted-foreground">
                            {t('liveGame.announceRecent', 'Recent')}
                        </p>
                        <div className="flex flex-wrap gap-2">
                            {(data.recent || []).slice(0, 6).map((a) => (
                                <button key={a.id} type="button" disabled={busy}
                                        onClick={() => send(a.text)}
                                        data-testid="live-game-announce-again"
                                        className="min-h-[40px] max-w-full truncate rounded-full border border-border px-3 text-xs font-semibold">
                                    {a.text?.en}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

export default AnnounceControl;
